import {
  BadgeCheck, Phone, Cloud, Network, PhoneForwarded, Headset,
  Wrench, ShieldCheck, Users, Settings, Check, Clock,
} from "lucide-react";
import PageIntro from "@/components/site/PageIntro";
import QuoteForm from "@/components/site/QuoteForm";
import { IMAGES, Reveal } from "@/components/site/anim";

const PRODUCTS = [
  {
    icon: Phone,
    title: "Cisco IP Phones",
    desc: "Cisco 7800 & 8800 Series desk phones and conference units — crystal-clear HD voice, built for busy teams.",
  },
  {
    icon: Cloud,
    title: "Webex Calling",
    desc: "Cloud-based calling that follows your people anywhere — desk, mobile and softphone on one number.",
  },
  {
    icon: Network,
    title: "Unified Comms Manager",
    desc: "Cisco UCM for on-prem and hybrid deployments — enterprise call control, presence and voicemail.",
  },
  {
    icon: PhoneForwarded,
    title: "SIP Trunking & Gateways",
    desc: "Reliable SIP trunks and Cisco voice gateways with number porting and carrier-grade redundancy.",
  },
];

const SUPPORT = [
  { icon: Settings, title: "Design & Provisioning", desc: "Right-sized system design, licensing and phone provisioning tailored to how you actually work." },
  { icon: Wrench, title: "Installation & Configuration", desc: "On-site and remote setup: dial plans, call routing, hunt groups, IVR and voicemail." },
  { icon: PhoneForwarded, title: "Number Porting & SIP", desc: "Zero-drama porting of your existing numbers and SIP trunk configuration with your carrier." },
  { icon: Cloud, title: "Migration to Cloud", desc: "Move from ageing PBX hardware to Webex Calling with a phased, no-downtime cutover." },
  { icon: ShieldCheck, title: "24/7 Monitoring & SLA", desc: "Proactive health checks, secure firmware updates and guaranteed response-time SLAs." },
  { icon: Users, title: "Onboarding & Training", desc: "Hands-on training so your staff are confident with handsets, apps and features from day one." },
];

const SLA = [
  { v: "24/7", l: "Support desk" },
  { v: "< 1 hr", l: "Priority response" },
  { v: "99.99%", l: "Voice uptime" },
  { v: "100%", l: "Number port success" },
];

export default function CiscoPartnerPage() {
  return (
    <div data-testid="cisco-page">
      <PageIntro
        overline="Cisco Partner"
        title="Cisco VoIP."
        subtitle="As a certified Cisco partner, Quadrim Tech sells, deploys and supports Cisco VoIP phone systems end to end — from handsets and cloud calling to 24/7 technical support."
      />

      {/* Partner badge band */}
      <section data-testid="cisco-badge" className="bg-base px-6 md:px-10 pb-8">
        <div className="mx-auto max-w-[1600px]">
          <Reveal className="flex flex-wrap items-center gap-4 rounded-2xl border border-white/10 bg-surface px-6 py-5">
            <BadgeCheck className="h-7 w-7 text-accent shrink-0" />
            <span className="font-heading text-lg md:text-xl font-medium tracking-tight">
              Certified Cisco Partner
            </span>
            <span className="hidden sm:block h-4 w-px bg-white/15" />
            <span className="text-[#A1A1AA] text-sm md:text-base">
              Authorised to sell, license and support the full Cisco collaboration and voice portfolio.
            </span>
          </Reveal>
        </div>
      </section>

      {/* Sales section */}
      <section id="voip-sales" data-testid="cisco-sales" className="bg-base py-20 md:py-28 px-6 md:px-10">
        <div className="mx-auto max-w-[1600px]">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
            <div className="lg:col-span-5">
              <Reveal className="relative overflow-hidden rounded-2xl border border-white/10 [clip-path:polygon(0_0,100%_0,100%_92%,0_100%)]">
                <img src={IMAGES.voip} alt="Cisco VoIP desk phone" className="h-[360px] md:h-[520px] w-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0B]/60 to-transparent" />
              </Reveal>
            </div>
            <div className="lg:col-span-7 lg:pt-4">
              <Reveal as="p" className="font-mono text-xs uppercase tracking-[0.25em] text-accent mb-5">
                [ 01 ] — Sales
              </Reveal>
              <Reveal delay={0.1} as="h2" className="font-heading font-light uppercase tracking-tight text-3xl md:text-5xl leading-[1.02] mb-6">
                VoIP phone systems,<br /> <span className="text-[#71717A]">sold and set up right.</span>
              </Reveal>
              <Reveal delay={0.15} as="p" className="text-[#A1A1AA] text-lg leading-relaxed max-w-xl mb-10">
                We help you choose the right Cisco solution — no over-selling. From a
                handful of handsets to a multi-site cloud calling rollout, we handle
                licensing, hardware and delivery.
              </Reveal>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                {PRODUCTS.map((p, i) => (
                  <Reveal
                    key={p.title}
                    delay={i * 0.08}
                    className="group rounded-2xl border border-white/10 bg-surface p-6 hover:-translate-y-1 hover:border-white/25 transition-all duration-300"
                  >
                    <p.icon className="h-7 w-7 text-accent mb-5 group-hover:scale-110 transition-transform duration-300" />
                    <h3 className="font-heading text-xl font-medium tracking-tight mb-2">{p.title}</h3>
                    <p className="text-sm text-[#A1A1AA] leading-relaxed">{p.desc}</p>
                  </Reveal>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Technical support section */}
      <section id="voip-support" data-testid="cisco-support" className="bg-[#0A0A0B] border-t border-white/10 py-20 md:py-28 px-6 md:px-10">
        <div className="mx-auto max-w-[1600px]">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14 md:mb-20">
            <div>
              <Reveal as="p" className="font-mono text-xs uppercase tracking-[0.25em] text-accent mb-5">
                [ 02 ] — Technical Support
              </Reveal>
              <Reveal delay={0.1} as="h2" className="font-heading font-light uppercase tracking-tight text-3xl md:text-5xl leading-[1.02]">
                We don't just sell it.<br />
                <span className="text-accent">We keep it running.</span>
              </Reveal>
            </div>
            <Reveal delay={0.2} as="p" className="max-w-sm text-[#A1A1AA] md:text-right flex items-center gap-3 md:justify-end">
              <Headset className="h-5 w-5 text-accent shrink-0" />
              Dedicated Cisco-trained engineers on call, day or night.
            </Reveal>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {SUPPORT.map((s, i) => (
              <Reveal
                key={s.title}
                delay={i * 0.06}
                className="group rounded-2xl border border-white/10 bg-surface p-7 hover:-translate-y-1 hover:border-white/25 transition-all duration-300"
              >
                <s.icon className="h-7 w-7 text-accent mb-5" />
                <h3 className="font-heading text-xl font-medium tracking-tight mb-2">{s.title}</h3>
                <p className="text-sm text-[#A1A1AA] leading-relaxed">{s.desc}</p>
              </Reveal>
            ))}
          </div>

          {/* SLA stats */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 mt-8">
            {SLA.map((s, i) => (
              <Reveal key={s.l} delay={i * 0.08} className="rounded-2xl border border-white/10 bg-surface p-6">
                <div className="font-heading text-3xl md:text-4xl font-black tracking-tight text-accent">{s.v}</div>
                <div className="font-mono text-[10px] uppercase tracking-[0.15em] text-[#71717A] mt-2">{s.l}</div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Quote request + CTA */}
      <section id="quote" data-testid="cisco-quote" className="relative bg-surface border-t border-white/10 py-20 md:py-28 px-6 md:px-10 overflow-hidden">
        <img src={IMAGES.support} alt="" aria-hidden="true" className="absolute inset-0 h-full w-full object-cover opacity-15" />
        <div className="absolute inset-0 bg-gradient-to-r from-surface via-surface/85 to-surface/60" />
        <div className="relative mx-auto max-w-[1600px]">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-start">
            <div className="lg:pt-6">
              <Reveal as="p" className="font-mono text-xs uppercase tracking-[0.25em] text-accent mb-5">
                [ 03 ] — Request a Quote
              </Reveal>
              <Reveal delay={0.05} as="h2" className="font-heading font-black uppercase tracking-tighter leading-[0.9] text-4xl md:text-6xl text-glow mb-8">
                Get a tailored<br />VoIP quote<span className="text-accent">.</span>
              </Reveal>
              <Reveal delay={0.1} as="p" className="text-[#A1A1AA] text-lg leading-relaxed max-w-lg mb-8">
                Tell us how many users and sites you have and we'll build a
                right-sized Cisco quote — sent straight to your inbox, no obligation.
              </Reveal>
              <ul className="space-y-3 mb-8">
                {["Free system assessment & fixed quote", "No-obligation migration plan", "Flexible support plans & SLAs"].map((t, i) => (
                  <Reveal as="li" key={t} delay={i * 0.06} className="flex items-center gap-3 text-[#A1A1AA]">
                    <span className="grid h-6 w-6 place-items-center rounded-full bg-accent/15 text-accent shrink-0">
                      <Check className="h-4 w-4" />
                    </span>
                    {t}
                  </Reveal>
                ))}
              </ul>

              <Reveal className="rounded-2xl border border-white/10 bg-[#0A0A0B]/70 backdrop-blur-sm p-6">
                <div className="flex items-center gap-3 mb-5">
                  <Clock className="h-5 w-5 text-accent" />
                  <span className="font-heading text-lg font-medium tracking-tight">How we onboard you</span>
                </div>
                <ol className="grid grid-cols-2 gap-x-6 gap-y-4">
                  {[
                    ["Assess", "Review your call volumes, sites and lines."],
                    ["Design & Quote", "Right-sized system, transparent pricing."],
                    ["Deploy", "Install, port numbers, configure — after hours."],
                    ["Support", "Monitoring, updates and a 24/7 desk."],
                  ].map(([t, d], i) => (
                    <li key={t} className="flex gap-3">
                      <span className="font-mono text-sm text-accent shrink-0">0{i + 1}</span>
                      <div>
                        <div className="font-medium text-white text-sm">{t}</div>
                        <div className="text-xs text-[#71717A] leading-relaxed">{d}</div>
                      </div>
                    </li>
                  ))}
                </ol>
              </Reveal>
            </div>

            <Reveal delay={0.1}>
              <QuoteForm />
            </Reveal>
          </div>
        </div>
      </section>
    </div>
  );
}
